import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { formatPlaytime, formatPrice, type GameSummaryDto } from '@shared/catalog';
import type { Cover as CoverData, PlayerPublic, RankingEntry } from '@shared/protocol';

export function Logo({ to = '/' }: { to?: string }) {
  return (
    <Link to={to} className="logo" data-nav-ignore>
      <span className="logo-mark">
        <i />
        <i />
        <i />
        <i />
      </span>
      <span>masa</span>
    </Link>
  );
}

export function Loading({ label }: { label?: string }) {
  return (
    <div className="page-loading">
      <div className="stack" style={{ alignItems: 'center' }}>
        <div className="spinner" />
        {label && <span className="muted">{label}</span>}
      </div>
    </div>
  );
}

export function Cover({ cover, className = '', children }: { cover: CoverData; className?: string; children?: ReactNode }) {
  return (
    <div className={`cover ${className}`} style={{ background: `linear-gradient(135deg, ${cover.gradient[0]}, ${cover.gradient[1]})` }}>
      {cover.imageUrl ? <img src={cover.imageUrl} alt="" loading="lazy" /> : <span className="emoji">{cover.emoji}</span>}
      {children}
    </div>
  );
}

export function Price({ cents, currency }: { cents: number; currency?: string }) {
  return <span className={`price ${cents === 0 ? 'free' : ''}`}>{formatPrice(cents, currency)}</span>;
}

export function Stars({ value }: { value: number | null }) {
  if (value === null) return <span className="faint">Henüz değerlendirme yok</span>;
  const full = Math.round(value);
  return (
    <span className="stars" aria-label={`${value} / 5`}>
      {'★'.repeat(full)}
      <span style={{ opacity: 0.3 }}>{'★'.repeat(5 - full)}</span>
    </span>
  );
}

export function GameCard({ game, to }: { game: GameSummaryDto; to: string }) {
  return (
    <Link to={to} className="game-card" data-focusable>
      <Cover cover={game.cover}>
        {game.owned && <span className="pill ok owned-badge">Kütüphanende</span>}
      </Cover>
      <div className="meta">
        <div className="title">{game.title}</div>
        <div className="sub">
          <span>👥 {game.minPlayers === game.maxPlayers ? game.minPlayers : `${game.minPlayers}-${game.maxPlayers}`}</span>
          <span>⏱ {game.durationMin} dk</span>
          {game.rating !== null && <span>★ {game.rating.toFixed(1)}</span>}
          <span className="spacer" />
          {game.playSeconds !== undefined ? (
            <span className="muted" title="Oynama süren">
              🕒 {game.playSeconds > 0 ? formatPlaytime(game.playSeconds) : 'Hiç oynanmadı'}
            </span>
          ) : game.owned ? (
            <span className="muted">Sahipsin</span>
          ) : (
            <Price cents={game.priceCents} currency={game.currency} />
          )}
        </div>
      </div>
    </Link>
  );
}

export function QRCodeView({ value, label }: { value: string; label?: string }) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    QRCode.toString(value, { type: 'svg', margin: 1, errorCorrectionLevel: 'M', color: { dark: '#0a0b14', light: '#ffffff' } })
      .then(setSvg)
      .catch(() => setSvg(''));
  }, [value]);
  return <div className="qr" role="img" aria-label={label ?? value} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function AvatarDot({ player, done, size }: { player: PlayerPublic; done?: boolean; size?: string }) {
  return (
    <span
      className={`avatar-dot ${player.connected ? '' : 'off'} ${done ? 'done' : ''}`}
      style={{ color: player.color, fontSize: size }}
      title={player.name}
    >
      {player.avatar}
    </span>
  );
}

export const DEVICE_LABEL: Record<string, string> = { tv: '📺 TV', mobile: '📱 Telefon', desktop: '💻 Bilgisayar' };
export const PLAY_MODE_LABEL: Record<string, string> = {
  tv_mobile: 'TV + telefon kumanda',
  tv_only: 'Büyük ekran gerekli',
  mobile_first: 'Telefon ağırlıklı',
};

export function Podium({ ranking, players }: { ranking: RankingEntry[]; players: PlayerPublic[] }) {
  const byId = new Map(players.map((p) => [p.id, p]));
  const top = ranking.slice(0, 3);
  const order = [top[1], top[0], top[2]].filter(Boolean) as RankingEntry[];
  return (
    <div>
      <div className="podium">
        {order.map((r) => (
          <div key={r.playerId} className="podium-step">
            <div className="av">{byId.get(r.playerId)?.avatar ?? '🙂'}</div>
            <div style={{ fontWeight: 800, fontSize: '1.3em' }}>{r.name}</div>
            <div className="muted">{r.score.toLocaleString('tr-TR')} puan</div>
            <div className="block">{r.rank}</div>
          </div>
        ))}
      </div>
      {ranking.length > 3 && (
        <div className="leaderboard" style={{ maxWidth: '32em', margin: '0 auto' }}>
          {ranking.slice(3).map((r) => (
            <div key={r.playerId} className="lb-row">
              <span className="rank">{r.rank}</span>
              <span className="av">{byId.get(r.playerId)?.avatar}</span>
              <span className="nm">{r.name}</span>
              <span />
              <span className="sc">{r.score.toLocaleString('tr-TR')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
